function isMinifiedFile(filename) {
  const minifiedPatterns = [
    /\.min\.js$/,
    /\.bundle\.js$/,
    /\.chunk\.js$/,
    /-[\w\d]{8,}\.js$/,
    // Hash-based filenames
    /Logger-[A-Za-z0-9]+\.js$/,
    // Logger bundle files
    /node_modules/,
    /dist\//,
    /build\//,
    /\.mjs$/
  ];
  return minifiedPatterns.some((pattern) => pattern.test(filename));
}
function extractCleanFilename(fullPath) {
  const filename = fullPath.split(/[/\\]/).pop() || fullPath;
  return filename.replace(/\.min\.js$/, ".js").replace(/\.bundle\.js$/, ".js").replace(/\.chunk\.js$/, ".js").replace(/-[\w\d]{8,}\.js$/, ".js").replace(/Logger-[A-Za-z0-9]+\.js$/, "logger.ts");
}
function parseStackTrace() {
  try {
    const stack = new Error().stack;
    if (!stack) {
      return null;
    }
    const lines = stack.split("\n").filter((line) => line.trim());
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line) {
        continue;
      }
      if (line.includes("parseStackTrace") || line.includes("Logger.") || line.includes(".log(") || line.includes("createStyledOutput")) {
        continue;
      }
      let match;
      const chromeMatch = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/);
      if (chromeMatch) {
        const filename = chromeMatch[2];
        if (filename && isMinifiedFile(filename)) {
          continue;
        }
        match = chromeMatch;
      } else {
        const firefoxMatch = line.match(/(.+?)@(.+?):(\d+):(\d+)$/);
        if (firefoxMatch) {
          const filename = firefoxMatch[2];
          if (filename && isMinifiedFile(filename)) {
            continue;
          }
          match = firefoxMatch;
        } else {
          const safariMatch = line.match(/(\S+)?@(.+?):(\d+):(\d+)$/);
          if (safariMatch) {
            const filename = safariMatch[2];
            if (filename && isMinifiedFile(filename)) {
              continue;
            }
            match = safariMatch;
          }
        }
      }
      if (!match) {
        continue;
      }
      const [, functionName, file, lineStr, columnStr] = match;
      if (!file) {
        continue;
      }
      const cleanFileName = extractCleanFilename(file).split("?")[0];
      if (!cleanFileName) {
        continue;
      }
      const lineNum = lineStr ? parseInt(lineStr, 10) : 0;
      const columnNum = columnStr ? parseInt(columnStr, 10) : 0;
      const cleanFunction = functionName && functionName.trim() ? functionName.trim() : void 0;
      return {
        file: cleanFileName ? cleanFileName : "unknown",
        line: lineNum,
        column: columnNum,
        function: cleanFunction
      };
    }
    return null;
  } catch {
    return null;
  }
}
const DEFAULT_CONFIG = {
  verbosity: "info",
  enableColors: true,
  enableTimestamps: true,
  enableStackTrace: true,
  theme: "default",
  bannerType: "simple",
  bufferSize: 1e3,
  autoDetectTheme: true,
  outputFormat: "auto"
};
const LEVEL_STYLES = {
  debug: {
    emoji: "🔍",
    label: "DEBUG",
    background: "linear-gradient(90deg, #6c757d, #495057)",
    color: "#ffffff",
    border: "1px solid #6c757d",
    shadow: "0 2px 4px rgba(108, 117, 125, 0.3)"
  },
  info: {
    emoji: "ℹ️",
    label: "INFO",
    background: "linear-gradient(90deg, #007bff, #0056b3)",
    color: "#ffffff",
    border: "1px solid #007bff",
    shadow: "0 2px 4px rgba(0, 123, 255, 0.3)"
  },
  warn: {
    emoji: "⚠️",
    label: "WARN",
    background: "linear-gradient(90deg, #ffc107, #e0a800)",
    color: "#000000",
    border: "1px solid #ffc107",
    shadow: "0 2px 4px rgba(255, 193, 7, 0.3)"
  },
  error: {
    emoji: "❌",
    label: "ERROR",
    background: "linear-gradient(90deg, #dc3545, #c82333)",
    color: "#ffffff",
    border: "1px solid #dc3545",
    shadow: "0 2px 4px rgba(220, 53, 69, 0.3)"
  },
  critical: {
    emoji: "🚨",
    label: "CRITICAL",
    background: "linear-gradient(90deg, #8B0000, #FF0000)",
    color: "#ffffff",
    border: "2px solid #FF0000",
    shadow: "0 4px 8px rgba(255, 0, 0, 0.4)"
  },
  success: {
    emoji: "✅",
    label: "SUCCESS",
    background: "linear-gradient(90deg, #28a745, #1e7e34)",
    color: "#ffffff",
    border: "1px solid #28a745",
    shadow: "0 2px 4px rgba(40, 167, 69, 0.3)"
  }
};
const BUFFER_LIMITS = {
  MIN_SIZE: 50,
  DEFAULT_SIZE: 1e3,
  MAX_SIZE: 1e4
};
const EXPORT_FORMATS = {
  json: { extension: ".json", mimeType: "application/json" },
  csv: { extension: ".csv", mimeType: "text/csv" },
  markdown: { extension: ".md", mimeType: "text/markdown" },
  plain: { extension: ".txt", mimeType: "text/plain" },
  html: { extension: ".html", mimeType: "text/html" }
};
const TIME_UNITS = {
  ms: 1,
  s: 1e3,
  m: 60 * 1e3,
  h: 60 * 60 * 1e3,
  d: 24 * 60 * 60 * 1e3
};
const ADAPTIVE_COLORS = {
  timestamp: {
    light: "#666666",
    dark: "#a0a0a0"
  },
  messageText: {
    light: "#2d3748",
    dark: "#f7fafc"
  },
  prefix: {
    light: "#2d3748",
    dark: "#e2e8f0"
  },
  prefixBackground: {
    light: "#2d3748",
    dark: "#4a5568"
  },
  location: {
    light: "#718096",
    dark: "#a0aec0"
  }
};
const BUILD_PRESETS = {
  /**
   * Configuración optimizada para Next.js builds
   */
  nextjs: {
    verbosity: "info",
    enableColors: true,
    enableTimestamps: false,
    enableStackTrace: false,
    autoDetectTheme: false,
    outputFormat: "build"
  },
  /**
   * Configuración para Webpack builds
   */
  webpack: {
    verbosity: "info",
    enableColors: true,
    enableTimestamps: true,
    enableStackTrace: false,
    autoDetectTheme: false,
    outputFormat: "build"
  },
  /**
   * Configuración para CI/CD environments
   */
  ci: {
    verbosity: "info",
    enableColors: false,
    enableTimestamps: true,
    enableStackTrace: true,
    autoDetectTheme: false,
    outputFormat: "ci"
  },
  /**
   * Configuración para desarrollo terminal
   */
  terminal: {
    verbosity: "debug",
    enableColors: true,
    enableTimestamps: true,
    enableStackTrace: true,
    autoDetectTheme: false,
    outputFormat: "ansi"
  }
};
const ENVIRONMENT_DETECTION = {
  // Next.js detection
  isNextJS: typeof process !== "undefined" && (process.env.NEXT_RUNTIME || process.env.NEXT_PUBLIC_VERCEL_ENV || process.argv && process.argv.some((arg) => arg.includes("next"))),
  // Webpack detection
  isWebpack: typeof process !== "undefined" && (process.env.WEBPACK_ENV || process.env.WEBPACK_BUILD || process.argv && process.argv.some((arg) => arg.includes("webpack"))),
  // CI/CD detection
  isCI: typeof process !== "undefined" && (process.env.CI || process.env.GITHUB_ACTIONS || process.env.JENKINS_URL || process.env.GITLAB_CI || process.env.TRAVIS || process.env.CIRCLECI),
  // Build detection
  isBuild: typeof process !== "undefined" && true,
  // Terminal with ANSI support
  isTerminal: typeof process !== "undefined" && (process.stdout?.isTTY === true && process.env.TERM !== "dumb")
};
function detectEnvironmentPreset() {
  if (ENVIRONMENT_DETECTION.isCI) {
    return "ci";
  }
  if (ENVIRONMENT_DETECTION.isNextJS) {
    return "nextjs";
  }
  if (ENVIRONMENT_DETECTION.isWebpack) {
    return "webpack";
  }
  if (ENVIRONMENT_DETECTION.isTerminal) {
    return "terminal";
  }
  return "terminal";
}
function getOptimalConfig() {
  const preset = detectEnvironmentPreset();
  return {
    ...DEFAULT_CONFIG,
    ...BUILD_PRESETS[preset]
  };
}
function formatTimestamp() {
  try {
    const now = /* @__PURE__ */ new Date();
    return now.toISOString();
  } catch {
    return (/* @__PURE__ */ new Date()).toISOString();
  }
}
function parseRelativeTime(timeStr) {
  const match = timeStr.match(/^(\d+)(ms|s|m|h|d)$/);
  if (!match) {
    throw new Error(`Invalid time format: ${timeStr}. Use format like "2h", "30m", "1d"`);
  }
  const [, amount, unit] = match;
  const multiplier = TIME_UNITS[unit];
  return parseInt(amount || "0", 10) * multiplier;
}
function parseTimeInput(input) {
  if (input instanceof Date) {
    return input;
  }
  if (typeof input === "number") {
    return new Date(Date.now() - input * TIME_UNITS.h);
  }
  if (typeof input === "string") {
    const isoDate = new Date(input);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }
    try {
      const ms = parseRelativeTime(input);
      return new Date(Date.now() - ms);
    } catch {
      throw new Error(`Invalid time format: ${input}`);
    }
  }
  throw new Error(`Unsupported time input type: ${typeof input}`);
}
function formatDisplayTime(date, format = "short") {
  switch (format) {
    case "time-only":
      return date.toTimeString().slice(0, 8);
    // HH:MM:SS
    case "full":
      return date.toISOString();
    case "short":
    default:
      return date.toISOString().slice(11, 23);
  }
}
class StyleBuilder {
  styles = [];
  constructor(baseStyle = "") {
    if (baseStyle) this.styles.push(baseStyle);
  }
  /**
   * Add background color or gradient
   */
  bg(background) {
    this.styles.push(`background: ${background}`);
    return this;
  }
  /**
   * Add text color
   */
  color(color) {
    this.styles.push(`color: ${color}`);
    return this;
  }
  /**
   * Add border styling
   */
  border(border) {
    this.styles.push(`border: ${border}`);
    return this;
  }
  /**
   * Add box shadow
   */
  shadow(shadow) {
    this.styles.push(`box-shadow: ${shadow}`);
    return this;
  }
  /**
   * Add padding
   */
  padding(padding) {
    this.styles.push(`padding: ${padding}`);
    return this;
  }
  /**
   * Add margin
   */
  margin(margin) {
    this.styles.push(`margin: ${margin}`);
    return this;
  }
  /**
   * Add border radius
   */
  rounded(radius = "4px") {
    this.styles.push(`border-radius: ${radius}`);
    return this;
  }
  /**
   * Add font weight
   */
  bold() {
    this.styles.push("font-weight: bold");
    return this;
  }
  /**
   * Add font styling
   */
  font(font) {
    this.styles.push(`font-family: ${font}`);
    return this;
  }
  /**
   * Set monospace font family (alias for common monospace fonts)
   */
  mono() {
    return this.font('Monaco, Consolas, "Courier New", monospace');
  }
  /**
   * Set system font family (alias for system fonts)
   */
  system() {
    return this.font('system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif');
  }
  /**
   * Add font size
   */
  size(size) {
    this.styles.push(`font-size: ${size}`);
    return this;
  }
  /**
   * Add line height
   */
  lineHeight(height) {
    this.styles.push(`line-height: ${height}`);
    return this;
  }
  /**
   * Add text decoration
   */
  underline() {
    this.styles.push("text-decoration: underline");
    return this;
  }
  /**
   * Add text transform
   */
  uppercase() {
    this.styles.push("text-transform: uppercase");
    return this;
  }
  /**
   * Add opacity
   */
  opacity(value) {
    this.styles.push(`opacity: ${value}`);
    return this;
  }
  /**
   * Add display property
   */
  display(value) {
    this.styles.push(`display: ${value}`);
    return this;
  }
  /**
   * Add position property
   */
  position(value) {
    this.styles.push(`position: ${value}`);
    return this;
  }
  /**
   * Add transform property
   */
  transform(value) {
    this.styles.push(`transform: ${value}`);
    return this;
  }
  /**
   * Add animation property
   */
  animation(value) {
    this.styles.push(`animation: ${value}`);
    return this;
  }
  /**
   * Add transition property
   */
  transition(value) {
    this.styles.push(`transition: ${value}`);
    return this;
  }
  /**
   * Add cursor property
   */
  cursor(value) {
    this.styles.push(`cursor: ${value}`);
    return this;
  }
  /**
   * Add any custom CSS property
   */
  custom(property, value) {
    this.styles.push(`${property}: ${value}`);
    return this;
  }
  /**
   * Add any CSS property (alias for custom)
   */
  css(property, value) {
    return this.custom(property, value);
  }
  /**
   * Build the final CSS string
   */
  build() {
    return this.styles.join("; ");
  }
  /**
   * Clear all styles and start fresh
   */
  clear() {
    this.styles = [];
    return this;
  }
  /**
   * Clone this StyleBuilder with the same styles
   */
  clone() {
    const cloned = new StyleBuilder();
    cloned.styles = [...this.styles];
    return cloned;
  }
  /**
   * Merge another StyleBuilder's styles into this one
   */
  merge(other) {
    this.styles.push(...other.styles);
    return this;
  }
}
function createStyler() {
  const builder = new StyleBuilder();
  return new Proxy(builder, {
    get(target, prop) {
      if (prop in target) {
        const method = target[prop];
        if (typeof method === "function") {
          return method.bind(target);
        }
        return method;
      }
      return void 0;
    }
  });
}
createStyler();
const StylePresets = {
  success: () => new StyleBuilder().bg("linear-gradient(135deg, #00b894 0%, #00a085 100%)").color("#ffffff").padding("4px 8px").rounded("4px").bold(),
  error: () => new StyleBuilder().bg("linear-gradient(135deg, #e84393 0%, #d63031 100%)").color("#ffffff").padding("4px 8px").rounded("4px").bold(),
  warning: () => new StyleBuilder().bg("linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)").color("#2d3436").padding("4px 8px").rounded("4px").bold(),
  info: () => new StyleBuilder().bg("linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)").color("#ffffff").padding("4px 8px").rounded("4px").bold(),
  debug: () => new StyleBuilder().bg("linear-gradient(135deg, #667eea 0%, #764ba2 100%)").color("#ffffff").padding("4px 8px").rounded("4px").bold(),
  muted: () => new StyleBuilder().color("#6c757d").font("Monaco, Consolas, monospace").size("12px"),
  accent: () => new StyleBuilder().bg("#f8f9fa").color("#495057").padding("2px 6px").rounded("3px").border("1px solid #dee2e6"),
  neon: () => new StyleBuilder().bg("linear-gradient(135deg, #0f3460 0%, #e94560 100%)").color("#00ffff").padding("4px 8px").rounded("4px").bold().shadow("0 0 10px rgba(0, 255, 255, 0.5)")
};
function getEnvironment() {
  if (typeof globalThis !== "undefined" && globalThis.Deno) {
    return "deno";
  }
  if (typeof window === "undefined" && typeof self !== "undefined" && typeof self.importScripts === "function") {
    return "webworker";
  }
  if (typeof process !== "undefined" && process.versions && process.versions.node) {
    if (isRunningInTerminal()) {
      return "terminal";
    }
    return "server";
  }
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    return "browser";
  }
  return "unknown";
}
function isRunningInTerminal() {
  if (typeof process === "undefined") return false;
  const isTTY = process.stdout && process.stdout.isTTY;
  const hasTerminalEnv = process.env && (process.env.TERM || process.env.TERM_PROGRAM || process.env.SSH_TTY || process.env.TERM_SESSION_ID);
  const terminalPrograms = [
    "vscode",
    "hyper",
    "iterm",
    "terminal",
    "alacritty",
    "kitty",
    "gnome-terminal",
    "konsole",
    "xterm",
    "tmux",
    "screen"
  ];
  const isTerminalProgram = hasTerminalEnv && terminalPrograms.some(
    (program) => process.env.TERM_PROGRAM?.toLowerCase().includes(program) || process.env.TERM?.toLowerCase().includes(program)
  );
  return Boolean(isTTY || hasTerminalEnv || isTerminalProgram);
}
function supportsANSI() {
  const env = getEnvironment();
  if (env === "browser") return false;
  if (env === "terminal") return true;
  if (env === "server") return checkServerANSISupport();
  return false;
}
function checkServerANSISupport() {
  if (typeof process === "undefined") return false;
  const supportsANSI2 = process.env && (process.env.COLORTERM || process.env.FORCE_COLOR || process.env.TERM && process.env.TERM !== "dumb" || process.env.TERM_PROGRAM);
  return Boolean(supportsANSI2);
}
function getColorCapability() {
  const env = getEnvironment();
  if (env === "browser") {
    return "full";
  }
  if (!supportsANSI()) {
    return "none";
  }
  if (typeof process !== "undefined" && process.env) {
    const hasTrueColor = process.env.COLORTERM === "truecolor" || process.env.COLORTERM === "24bit";
    const has256Colors = process.env.TERM && process.env.TERM.includes("256");
    if (hasTrueColor || has256Colors) {
      return "full";
    }
  }
  return "basic";
}
function getTerminalWidth() {
  if (typeof process !== "undefined" && process.stdout?.columns) {
    return process.stdout.columns;
  }
  return 80;
}
function getTerminalHeight() {
  if (typeof process !== "undefined" && process.stdout?.rows) {
    return process.stdout.rows;
  }
  return 24;
}
function getEnvironmentInfo() {
  return {
    environment: getEnvironment(),
    supportsANSI: supportsANSI(),
    colorCapability: getColorCapability(),
    isTTY: typeof process !== "undefined" ? Boolean(process.stdout?.isTTY) : false,
    platform: typeof process !== "undefined" ? process.platform : "unknown",
    nodeVersion: typeof process !== "undefined" ? process.versions?.node : null,
    term: typeof process !== "undefined" ? process.env?.TERM : null,
    colorTerm: typeof process !== "undefined" ? process.env?.COLORTERM : null,
    terminalWidth: getTerminalWidth(),
    terminalHeight: getTerminalHeight()
  };
}
const ANSI_16_COLORS = {
  black: { fg: "30", bg: "40", bright: { fg: "90", bg: "100" } },
  red: { fg: "31", bg: "41", bright: { fg: "91", bg: "101" } },
  green: { fg: "32", bg: "42", bright: { fg: "92", bg: "102" } },
  yellow: { fg: "33", bg: "43", bright: { fg: "93", bg: "103" } },
  blue: { fg: "34", bg: "44", bright: { fg: "94", bg: "104" } },
  magenta: { fg: "35", bg: "45", bright: { fg: "95", bg: "105" } },
  cyan: { fg: "36", bg: "46", bright: { fg: "96", bg: "106" } },
  white: { fg: "37", bg: "47", bright: { fg: "97", bg: "107" } },
  gray: { fg: "90", bg: "100", bright: { fg: "37", bg: "47" } },
  grey: { fg: "90", bg: "100", bright: { fg: "37", bg: "47" } }
};
const CSS_NAMED_COLORS = {
  aliceblue: "#f0f8ff",
  antiquewhite: "#faebd7",
  aqua: "#00ffff",
  aquamarine: "#7fffd4",
  azure: "#f0ffff",
  beige: "#f5f5dc",
  bisque: "#ffe4c4",
  black: "#000000",
  blanchedalmond: "#ffebcd",
  blue: "#0000ff",
  blueviolet: "#8a2be2",
  brown: "#a52a2a",
  burlywood: "#deb887",
  cadetblue: "#5f9ea0",
  chartreuse: "#7fff00",
  chocolate: "#d2691e",
  coral: "#ff7f50",
  cornflowerblue: "#6495ed",
  cornsilk: "#fff8dc",
  crimson: "#dc143c",
  cyan: "#00ffff",
  darkblue: "#00008b",
  darkcyan: "#008b8b",
  darkgoldenrod: "#b8860b",
  darkgray: "#a9a9a9",
  darkgreen: "#006400",
  darkkhaki: "#bdb76b",
  darkmagenta: "#8b008b",
  darkolivegreen: "#556b2f",
  darkorange: "#ff8c00",
  darkorchid: "#9932cc",
  darkred: "#8b0000",
  darksalmon: "#e9967a",
  darkseagreen: "#8fbc8f",
  darkslateblue: "#483d8b",
  darkslategray: "#2f4f4f",
  darkturquoise: "#00ced1",
  darkviolet: "#9400d3",
  deeppink: "#ff1493",
  deepskyblue: "#00bfff",
  dimgray: "#696969",
  dodgerblue: "#1e90ff",
  firebrick: "#b22222",
  floralwhite: "#fffaf0",
  forestgreen: "#228b22",
  fuchsia: "#ff00ff",
  gainsboro: "#dcdcdc",
  ghostwhite: "#f8f8ff",
  gold: "#ffd700",
  goldenrod: "#daa520",
  gray: "#808080",
  green: "#008000",
  greenyellow: "#adff2f",
  honeydew: "#f0fff0",
  hotpink: "#ff69b4",
  indianred: "#cd5c5c",
  indigo: "#4b0082",
  ivory: "#fffff0",
  khaki: "#f0e68c",
  lavender: "#e6e6fa",
  lavenderblush: "#fff0f5",
  lawngreen: "#7cfc00",
  lemonchiffon: "#fffacd",
  lightblue: "#add8e6",
  lightcoral: "#f08080",
  lightcyan: "#e0ffff",
  lightgoldenrodyellow: "#fafad2",
  lightgray: "#d3d3d3",
  lightgreen: "#90ee90",
  lightpink: "#ffb6c1",
  lightsalmon: "#ffa07a",
  lightseagreen: "#20b2aa",
  lightskyblue: "#87cefa",
  lightslategray: "#778899",
  lightsteelblue: "#b0c4de",
  lightyellow: "#ffffe0",
  lime: "#00ff00",
  limegreen: "#32cd32",
  linen: "#faf0e6",
  magenta: "#ff00ff",
  maroon: "#800000",
  mediumaquamarine: "#66cdaa",
  mediumblue: "#0000cd",
  mediumorchid: "#ba55d3",
  mediumpurple: "#9370db",
  mediumseagreen: "#3cb371",
  mediumslateblue: "#7b68ee",
  mediumspringgreen: "#00fa9a",
  mediumturquoise: "#48d1cc",
  mediumvioletred: "#c71585",
  midnightblue: "#191970",
  mintcream: "#f5fffa",
  mistyrose: "#ffe4e1",
  moccasin: "#ffe4b5",
  navajowhite: "#ffdead",
  navy: "#000080",
  oldlace: "#fdf5e6",
  olive: "#808000",
  olivedrab: "#6b8e23",
  orange: "#ffa500",
  orangered: "#ff4500",
  orchid: "#da70d6",
  palegoldenrod: "#eee8aa",
  palegreen: "#98fb98",
  paleturquoise: "#afeeee",
  palevioletred: "#db7093",
  papayawhip: "#ffefd5",
  peachpuff: "#ffdab9",
  peru: "#cd853f",
  pink: "#ffc0cb",
  plum: "#dda0dd",
  powderblue: "#b0e0e6",
  purple: "#800080",
  rebeccapurple: "#663399",
  red: "#ff0000",
  rosybrown: "#bc8f8f",
  royalblue: "#4169e1",
  saddlebrown: "#8b4513",
  salmon: "#fa8072",
  sandybrown: "#f4a460",
  seagreen: "#2e8b57",
  seashell: "#fff5ee",
  sienna: "#a0522d",
  silver: "#c0c0c0",
  skyblue: "#87ceeb",
  slateblue: "#6a5acd",
  slategray: "#708090",
  snow: "#fffafa",
  springgreen: "#00ff7f",
  steelblue: "#4682b4",
  tan: "#d2b48c",
  teal: "#008080",
  thistle: "#d8bfd8",
  tomato: "#ff6347",
  turquoise: "#40e0d0",
  violet: "#ee82ee",
  wheat: "#f5deb3",
  white: "#ffffff",
  whitesmoke: "#f5f5f5",
  yellow: "#ffff00",
  yellowgreen: "#9acd32"
};
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result || !result[1] || !result[2] || !result[3]) {
    return { r: 255, g: 255, b: 255 };
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  };
}
function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}
function hexTo256(hex) {
  const { r, g, b } = hexToRgb(hex);
  if (r === g && g === b) {
    if (r < 8) return 16;
    if (r > 248) return 231;
    return Math.round((r - 8) / 247 * 24) + 232;
  }
  const ri = Math.round(r / 255 * 5);
  const gi = Math.round(g / 255 * 5);
  const bi = Math.round(b / 255 * 5);
  return 16 + 36 * ri + 6 * gi + bi;
}
function colorToHex(color) {
  if (color.startsWith("#")) {
    return color;
  }
  const named = CSS_NAMED_COLORS[color.toLowerCase()];
  if (named) {
    return named;
  }
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch && rgbMatch[1] && rgbMatch[2] && rgbMatch[3]) {
    return rgbToHex(
      parseInt(rgbMatch[1]),
      parseInt(rgbMatch[2]),
      parseInt(rgbMatch[3])
    );
  }
  return "#ffffff";
}
function getANSIForeground(color, capability) {
  if (capability === "none") return "";
  const lowerColor = color.toLowerCase();
  if (capability === "basic" && ANSI_16_COLORS[lowerColor]) {
    return `\x1B[${ANSI_16_COLORS[lowerColor].fg}m`;
  }
  if (capability === "basic") {
    const hex2 = colorToHex(color);
    const nearestBasic = findNearestBasicColor(hex2);
    const basicColor = ANSI_16_COLORS[nearestBasic];
    return basicColor ? `\x1B[${basicColor.fg}m` : "\x1B[37m";
  }
  const hex = colorToHex(color);
  const { r, g, b } = hexToRgb(hex);
  return `\x1B[38;2;${r};${g};${b}m`;
}
function getANSIBackground(color, capability) {
  if (capability === "none") return "";
  const lowerColor = color.toLowerCase();
  if (capability === "basic" && ANSI_16_COLORS[lowerColor]) {
    return `\x1B[${ANSI_16_COLORS[lowerColor].bg}m`;
  }
  if (capability === "basic") {
    const hex2 = colorToHex(color);
    const nearestBasic = findNearestBasicColor(hex2);
    const basicColor = ANSI_16_COLORS[nearestBasic];
    return basicColor ? `\x1B[${basicColor.bg}m` : "\x1B[47m";
  }
  const hex = colorToHex(color);
  const { r, g, b } = hexToRgb(hex);
  return `\x1B[48;2;${r};${g};${b}m`;
}
function getANSI256Foreground(colorIndex) {
  return `\x1B[38;5;${colorIndex}m`;
}
function getANSI256Background(colorIndex) {
  return `\x1B[48;5;${colorIndex}m`;
}
function findNearestBasicColor(hex) {
  const { r, g, b } = hexToRgb(hex);
  const basicColors = {
    black: { r: 0, g: 0, b: 0 },
    red: { r: 205, g: 0, b: 0 },
    green: { r: 0, g: 205, b: 0 },
    yellow: { r: 205, g: 205, b: 0 },
    blue: { r: 0, g: 0, b: 238 },
    magenta: { r: 205, g: 0, b: 205 },
    cyan: { r: 0, g: 205, b: 205 },
    white: { r: 229, g: 229, b: 229 }
  };
  let nearest = "white";
  let minDistance = Infinity;
  for (const [name, rgb] of Object.entries(basicColors)) {
    const distance = Math.sqrt(
      Math.pow(r - rgb.r, 2) + Math.pow(g - rgb.g, 2) + Math.pow(b - rgb.b, 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearest = name;
    }
  }
  return nearest;
}
function extractGradientColors(gradient) {
  const hexMatches = gradient.match(/#[a-fA-F0-9]{6}/g) || [];
  const rgbMatches = gradient.match(/rgba?\([^)]+\)/g) || [];
  const namedMatches = gradient.match(/,\s*([a-z]+)(?:\s|,|\))/gi) || [];
  const colors = [
    ...hexMatches,
    ...rgbMatches.map(colorToHex),
    ...namedMatches.map((m) => {
      const name = m.replace(/[,\s)]/g, "").toLowerCase();
      return CSS_NAMED_COLORS[name] || null;
    }).filter((c) => c !== null)
  ];
  return colors.length > 0 ? colors : ["#ffffff"];
}
function gradientToSingleColor(gradient) {
  const colors = extractGradientColors(gradient);
  if (colors.length === 0) return "#ffffff";
  if (colors.length === 1) return colors[0] ?? "#ffffff";
  const rgbs = colors.map((c) => hexToRgb(colorToHex(c)));
  const avgR = Math.round(rgbs.reduce((sum, rgb) => sum + rgb.r, 0) / rgbs.length);
  const avgG = Math.round(rgbs.reduce((sum, rgb) => sum + rgb.g, 0) / rgbs.length);
  const avgB = Math.round(rgbs.reduce((sum, rgb) => sum + rgb.b, 0) / rgbs.length);
  return rgbToHex(avgR, avgG, avgB);
}
function cssColorToANSI(cssColor, capability, isBackground = false) {
  if (capability === "none") return "";
  let resolvedColor;
  if (cssColor.includes("gradient")) {
    resolvedColor = gradientToSingleColor(cssColor);
  } else {
    resolvedColor = colorToHex(cssColor);
  }
  return isBackground ? getANSIBackground(resolvedColor, capability) : getANSIForeground(resolvedColor, capability);
}
const ANSI = {
  reset: "\x1B[0m",
  bold: "\x1B[1m",
  dim: "\x1B[2m",
  italic: "\x1B[3m",
  underline: "\x1B[4m",
  blink: "\x1B[5m",
  inverse: "\x1B[7m",
  hidden: "\x1B[8m",
  strikethrough: "\x1B[9m",
  fg: {
    black: "\x1B[30m",
    red: "\x1B[31m",
    green: "\x1B[32m",
    yellow: "\x1B[33m",
    blue: "\x1B[34m",
    magenta: "\x1B[35m",
    cyan: "\x1B[36m",
    white: "\x1B[37m",
    gray: "\x1B[90m",
    brightRed: "\x1B[91m",
    brightGreen: "\x1B[92m",
    brightYellow: "\x1B[93m",
    brightBlue: "\x1B[94m",
    brightMagenta: "\x1B[95m",
    brightCyan: "\x1B[96m",
    brightWhite: "\x1B[97m"
  },
  bg: {
    black: "\x1B[40m",
    red: "\x1B[41m",
    green: "\x1B[42m",
    yellow: "\x1B[43m",
    blue: "\x1B[44m",
    magenta: "\x1B[45m",
    cyan: "\x1B[46m",
    white: "\x1B[47m",
    gray: "\x1B[100m",
    brightRed: "\x1B[101m",
    brightGreen: "\x1B[102m",
    brightYellow: "\x1B[103m",
    brightBlue: "\x1B[104m",
    brightMagenta: "\x1B[105m",
    brightCyan: "\x1B[106m",
    brightWhite: "\x1B[107m"
  },
  rgb: (r, g, b) => `\x1B[38;2;${r};${g};${b}m`,
  bgRgb: (r, g, b) => `\x1B[48;2;${r};${g};${b}m`,
  color256: (n) => `\x1B[38;5;${n}m`,
  bgColor256: (n) => `\x1B[48;5;${n}m`
};
class TerminalRenderer {
  colorCapability;
  constructor(colorCapability = "full") {
    this.colorCapability = colorCapability;
  }
  /**
   * Get ANSI color codes - now supports truecolor/256-color
   */
  getColorCode(color, background = false) {
    return background ? getANSIBackground(color, this.colorCapability) : getANSIForeground(color, this.colorCapability);
  }
  /**
   * Get ANSI style codes
   */
  getStyleCode(styles) {
    const styleCodes = {
      "bold": "1",
      "dim": "2",
      "italic": "3",
      "underline": "4",
      "reset": "0"
    };
    const codes = styles.map((style) => styleCodes[style] || "0").join(";");
    return codes ? `\x1B[${codes}m` : "";
  }
  /**
   * Convert log styles to ANSI formatting
   */
  renderTerminal(level, message, timestamp, prefix, location, styles) {
    const reset = "\x1B[0m";
    const result = { text: "", reset };
    if (styles) {
      result.timestamp = this.styleTimestamp(timestamp, styles.timestamp);
      result.level = this.styleLevel(level, styles.level);
      result.prefix = this.stylePrefix(prefix, styles.prefix);
      result.message = this.styleMessage(message, styles.message);
      result.location = this.styleLocation(location, styles.location);
    } else {
      result.timestamp = this.styleTimestamp(timestamp);
      result.level = this.styleLevel(level);
      result.prefix = this.stylePrefix(prefix);
      result.message = this.styleMessage(message);
      result.location = this.styleLocation(location);
    }
    const parts = [];
    if (result.timestamp) parts.push(result.timestamp);
    if (result.level) parts.push(result.level);
    if (result.prefix) parts.push(result.prefix);
    if (result.message) parts.push(result.message);
    if (result.location) parts.push(result.location);
    result.text = parts.join(" ") + reset;
    return result;
  }
  /**
   * Style timestamp based on preset or defaults
   */
  styleTimestamp(timestamp, style) {
    if (!timestamp || !style?.show) return "";
    if (this.colorCapability === "none") {
      return timestamp;
    }
    const dimStyle = this.getStyleCode(["dim"]);
    return `${dimStyle}${timestamp}${"\x1B[0m"}`;
  }
  /**
   * Style level with appropriate colors and formatting
   */
  styleLevel(level, style) {
    if (!style?.show) return "";
    const levelText = this.getLevelText(level);
    const levelColors = {
      "debug": "magenta",
      "info": "blue",
      "warn": "yellow",
      "error": "red",
      "critical": "red"
    };
    if (this.colorCapability === "none") {
      return `[${levelText}]`;
    }
    const color = levelColors[level] || "white";
    const colorCode = this.getColorCode(color);
    const boldStyle = this.getStyleCode(["bold"]);
    if (style?.style === "compact") {
      return `${colorCode}[${levelText}]${"\x1B[0m"}`;
    } else {
      return `${colorCode}${boldStyle}[${levelText}]${"\x1B[0m"}`;
    }
  }
  /**
   * Style prefix based on preset - now as a badge
   */
  stylePrefix(prefix, style) {
    if (!prefix || !style?.show) return "";
    if (this.colorCapability === "none") {
      return `[${prefix}]`;
    }
    const colorCode = this.getColorCode("cyan");
    const bgBlack = "\x1B[40m";
    return `${bgBlack}${colorCode}[${prefix.toUpperCase()}]${"\x1B[0m"}`;
  }
  /**
   * Style message based on preset
   */
  styleMessage(message, style) {
    if (!style?.show) return message;
    return message;
  }
  /**
   * Style location (file:line) information
   */
  styleLocation(location, style) {
    if (!location || !style?.show) return "";
    if (this.colorCapability === "none") {
      return `(${location})`;
    }
    const dimStyle = this.getStyleCode(["dim"]);
    if (style?.style === "clickable") {
      const underlineStyle = this.getStyleCode(["dim", "underline"]);
      return `${underlineStyle}(${location})${"\x1B[0m"}`;
    }
    return `${dimStyle}(${location})${"\x1B[0m"}`;
  }
  /**
   * Get text representation of log level
   */
  getLevelText(level) {
    const levelMap = {
      "debug": "DEBUG",
      "info": "INFO",
      "warn": "WARN",
      "error": "ERROR",
      "critical": "CRITICAL"
    };
    return levelMap[level] || String(level).toUpperCase();
  }
  /**
   * Create cyberpunk-style ANSI rendering with truecolor support
   */
  renderCyberpunk(level, message, timestamp, prefix, location) {
    const reset = ANSI.reset;
    const cyberpunkColors = {
      "debug": "#ff00ff",
      // Neon magenta
      "info": "#00ffff",
      // Neon cyan
      "warn": "#ffff00",
      // Neon yellow
      "error": "#ff0040",
      // Neon red-pink
      "critical": "#ff0000"
      // Pure red
    };
    const parts = [];
    if (timestamp) {
      parts.push(`${ANSI.dim}${timestamp}${reset}`);
    }
    const levelColor = this.getColorCode(cyberpunkColors[level] || "#ffffff");
    const bgBlack = ANSI.bg.black;
    const bold = level === "critical" ? ANSI.bold : "";
    parts.push(`${bgBlack}${levelColor}${bold} ${this.getLevelText(level)} ${reset}`);
    if (prefix) {
      const cyanColor = this.getColorCode("#00ffff");
      parts.push(`${bgBlack}${cyanColor}[${prefix.toUpperCase()}]${reset}`);
    }
    parts.push(message);
    if (location) {
      parts.push(`${ANSI.dim}(${location})${reset}`);
    }
    return {
      text: parts.join(" "),
      reset
    };
  }
  /**
   * Create minimal ANSI rendering with truecolor support
   */
  renderMinimal(level, message, timestamp, prefix) {
    const reset = ANSI.reset;
    const minimalColors = {
      "debug": "#c678dd",
      // Soft purple
      "info": "#61afef",
      // Soft blue
      "warn": "#e5c07b",
      // Soft yellow
      "error": "#e06c75",
      // Soft red
      "critical": "#be5046"
      // Dark red
    };
    const parts = [];
    if (timestamp) {
      parts.push(`${ANSI.dim}${timestamp}${reset}`);
    }
    const levelColor = this.getColorCode(minimalColors[level] || "#abb2bf");
    const bold = level === "critical" ? ANSI.bold : "";
    parts.push(`${levelColor}${bold}${this.getLevelText(level)}:${reset}`);
    if (prefix) {
      const cyanColor = this.getColorCode("#56b6c2");
      parts.push(`${cyanColor}[${prefix.toUpperCase()}]${reset}`);
    }
    parts.push(message);
    return {
      text: parts.join(" "),
      reset
    };
  }
  /**
   * Get chalk-like interface for log level with truecolor support
   */
  getChalkForLevel(level) {
    const levelColors = {
      "debug": "#c678dd",
      "info": "#61afef",
      "warn": "#e5c07b",
      "error": "#e06c75",
      "critical": "#be5046"
    };
    const reset = ANSI.reset;
    return {
      color: (text) => {
        const color = levelColors[level] || "#abb2bf";
        const colorCode = this.getColorCode(color);
        return `${colorCode}${text}${reset}`;
      },
      bold: (text) => `${ANSI.bold}${text}${reset}`,
      dim: (text) => `${ANSI.dim}${text}${reset}`,
      bgGray: (text) => `${ANSI.bg.gray}${text}${reset}`,
      bgBlue: (text) => `${this.getColorCode("#1e3a5f", true)}${text}${reset}`,
      reset: (text) => text,
      cyan: {
        bold: (text) => {
          const cyan = this.getColorCode("#00ffff");
          return `${cyan}${ANSI.bold}${text}${reset}`;
        },
        dim: (text) => {
          const cyan = this.getColorCode("#00ffff");
          return `${cyan}${ANSI.dim}${text}${reset}`;
        },
        bg: (text) => {
          const cyan = this.getColorCode("#00ffff");
          return `${ANSI.bg.black}${cyan}[${text.toUpperCase()}]${reset}`;
        }
      }
    };
  }
}
class CSS2ANSIAdapter {
  renderer;
  constructor() {
    const colorCapability = getColorCapability();
    this.renderer = new TerminalRenderer(colorCapability);
  }
  /**
   * Convert LogStyles to ANSIStyle for terminal rendering
   */
  adaptStyles(level, message, timestamp, prefix, location, styles, presetName) {
    if (presetName) {
      switch (presetName) {
        case "cyberpunk":
          return this.renderer.renderCyberpunk(level, message, timestamp, prefix, location);
        case "minimal":
          return this.renderer.renderMinimal(level, message, timestamp, prefix);
        case "production":
          return this.adaptProductionStyles(level, message, timestamp, prefix, location, styles);
        case "debug":
          return this.adaptDebugStyles(level, message, timestamp, prefix, location, styles);
        case "glassmorphism":
          return this.adaptGlassmorphismStyles(level, message, timestamp, prefix, location, styles);
      }
    }
    return this.renderer.renderTerminal(level, message, timestamp, prefix, location, styles);
  }
  /**
   * Adapt production preset for terminal
   */
  adaptProductionStyles(level, message, timestamp, prefix, location, styles) {
    const reset = "\x1B[0m";
    const levelChalk = this.renderer.getChalkForLevel(level);
    const parts = [];
    if (timestamp && styles?.timestamp?.show) {
      parts.push(levelChalk.dim(timestamp));
    }
    if (styles?.level?.show) {
      parts.push(levelChalk.color(`[${this.renderer["getLevelText"](level)}]`));
    }
    if (prefix && styles?.prefix?.show) {
      parts.push(levelChalk.dim(`[${prefix}]`));
    }
    if (message && styles?.message?.show) {
      parts.push(levelChalk.reset(message));
    }
    if (location && styles?.location?.show) {
      parts.push(levelChalk.dim(`(${location})`));
    }
    return {
      text: parts.join(" ") + reset,
      reset
    };
  }
  /**
   * Adapt debug preset for terminal
   */
  adaptDebugStyles(level, message, timestamp, prefix, location, styles) {
    const reset = "\x1B[0m";
    const levelChalk = this.renderer.getChalkForLevel(level);
    const parts = [];
    if (timestamp && styles?.timestamp?.show) {
      parts.push(levelChalk.dim(timestamp));
    }
    if (styles?.level?.show) {
      if (styles?.level?.style === "compact") {
        parts.push(levelChalk.color(`[${this.renderer["getLevelText"](level)}]`));
      } else {
        parts.push(levelChalk.bold(`[${this.renderer["getLevelText"](level)}]`));
      }
    }
    if (prefix && styles?.prefix?.show) {
      if (styles?.prefix?.style === "compact") {
        parts.push(levelChalk.cyan.dim(`[${prefix}]`));
      } else {
        parts.push(levelChalk.cyan.bold(`[${prefix}]`));
      }
    }
    if (message && styles?.message?.show) {
      parts.push(levelChalk.reset(message));
    }
    if (location && styles?.location?.show) {
      if (styles?.location?.style === "clickable") {
        parts.push(levelChalk.dim(`${location} (clickable)`));
      } else {
        parts.push(levelChalk.dim(`(${location})`));
      }
    }
    return {
      text: parts.join(" ") + reset,
      reset
    };
  }
  /**
   * Adapt glassmorphism preset for terminal
   */
  adaptGlassmorphismStyles(level, message, timestamp, prefix, location, styles) {
    const reset = "\x1B[0m";
    const levelChalk = this.renderer.getChalkForLevel(level);
    const parts = [];
    if (timestamp && styles?.timestamp?.show) {
      parts.push(levelChalk.dim(timestamp));
    }
    if (styles?.level?.show) {
      parts.push(levelChalk.bgGray(` ${this.renderer["getLevelText"](level)} `));
    }
    if (prefix && styles?.prefix?.show) {
      parts.push(levelChalk.bgBlue(`[${prefix}]`));
    }
    if (message && styles?.message?.show) {
      parts.push(levelChalk.reset(message));
    }
    if (location && styles?.location?.show) {
      parts.push(levelChalk.dim(`(${location})`));
    }
    return {
      text: parts.join(" ") + reset,
      reset
    };
  }
  /**
   * Convert CSS color values to ANSI-friendly colors
   */
  adaptColor(cssColor) {
    if (cssColor.startsWith("#")) {
      return cssColor;
    }
    if (cssColor.startsWith("rgb(")) {
      return cssColor;
    }
    if (cssColor.includes("gradient")) {
      return this.extractColorFromGradient(cssColor);
    }
    return this.cssNameToANSI(cssColor);
  }
  /**
   * Extract primary color from CSS gradient
   */
  extractColorFromGradient(gradient) {
    const match = gradient.match(/#[0-9a-fA-F]{6}|rgb\([^)]+\)/);
    return match ? match[0] : "#ffffff";
  }
  /**
   * Convert CSS color names to ANSI-compatible names
   */
  cssNameToANSI(cssColor) {
    const colorMap = {
      "black": "black",
      "white": "white",
      "red": "red",
      "green": "green",
      "blue": "blue",
      "yellow": "yellow",
      "cyan": "cyan",
      "magenta": "magenta",
      "gray": "gray",
      "grey": "gray",
      "orange": "yellow",
      "purple": "magenta",
      "pink": "red",
      "brown": "yellow",
      "lightblue": "cyan",
      "lightgreen": "green",
      "lightgray": "gray",
      "lightgrey": "gray"
    };
    return colorMap[cssColor.toLowerCase()] || "white";
  }
  /**
   * Check if a style should be rendered in terminal
   */
  shouldRenderStyle(styleType, styles) {
    if (!styles) return true;
    const styleConfig = styles[styleType];
    return styleConfig?.show !== false;
  }
  /**
   * Get renderer instance for advanced usage
   */
  getRenderer() {
    return this.renderer;
  }
}
const css2ansiAdapter = new CSS2ANSIAdapter();
function adaptToTerminal(level, message, timestamp, prefix, location, styles, presetName) {
  return css2ansiAdapter.adaptStyles(level, message, timestamp, prefix, location, styles, presetName);
}
function detectDevToolsTheme() {
  try {
    if (typeof window === "undefined" || !window.matchMedia) {
      return "light";
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    return mediaQuery.matches ? "dark" : "light";
  } catch (error) {
    console.warn("Failed to detect DevTools theme:", error);
    return "light";
  }
}
function getAdaptiveColor(colors, theme) {
  const currentTheme = theme ?? detectDevToolsTheme();
  return colors[currentTheme];
}
function setupThemeChangeListener(callback) {
  try {
    if (typeof window === "undefined" || !window.matchMedia) {
      return null;
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      callback(e.matches ? "dark" : "light");
    };
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener?.(handler);
    }
    return null;
  } catch (error) {
    console.warn("Failed to set up theme change listener:", error);
    return null;
  }
}
function createStyledOutput(level, levelStyles, prefix, message, stackInfo, autoDetectTheme = true, presetStyles, presetName) {
  const levelConfig = levelStyles[level];
  const timestamp = formatTimestamp();
  const environment = getEnvironment();
  if (environment !== "browser" && supportsANSI()) {
    return createTerminalOutput(level, message, timestamp, prefix, stackInfo, presetStyles, presetName);
  }
  const currentTheme = autoDetectTheme ? detectDevToolsTheme() : "light";
  const timestampStyle = new StyleBuilder().color(getAdaptiveColor(ADAPTIVE_COLORS.timestamp, currentTheme)).size("11px").font("Monaco, Consolas, monospace").build();
  const levelStyle = new StyleBuilder().bg(levelConfig.background).color(levelConfig.color).border(levelConfig.border).shadow(levelConfig.shadow).padding("2px 8px").rounded("4px").bold().font("Monaco, Consolas, monospace").size("12px").build();
  const prefixStyle = new StyleBuilder().bg(getAdaptiveColor(ADAPTIVE_COLORS.prefixBackground, currentTheme)).color(getAdaptiveColor(ADAPTIVE_COLORS.prefix, currentTheme)).padding("2px 6px").rounded("3px").bold().font("Monaco, Consolas, monospace").size("11px").build();
  const messageStyle = new StyleBuilder().color(getAdaptiveColor(ADAPTIVE_COLORS.messageText, currentTheme)).font("system-ui, -apple-system, sans-serif").size("14px").build();
  const locationStyle = new StyleBuilder().color(getAdaptiveColor(ADAPTIVE_COLORS.location, currentTheme)).size("11px").font("Monaco, Consolas, monospace").build();
  let format = `%c${timestamp.slice(11, 23)} %c${levelConfig.emoji} ${levelConfig.label}`;
  const styles = [timestampStyle, levelStyle];
  if (prefix) {
    format += ` %c${prefix}`;
    styles.push(prefixStyle);
  }
  format += ` %c${message}`;
  styles.push(messageStyle);
  if (stackInfo) {
    format += ` %c(${stackInfo.file}:${stackInfo.line}:${stackInfo.column})`;
    styles.push(locationStyle);
  }
  return [format, ...styles];
}
function generateLogId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
function safeStringify(obj, _maxDepth = 3) {
  try {
    return JSON.stringify(obj, (_key, value) => {
      if (typeof value === "function") return "[Function]";
      if (value instanceof Error) return `[Error: ${value.message}]`;
      if (value instanceof Date) return value.toISOString();
      if (typeof value === "undefined") return "[undefined]";
      return value;
    }, 2);
  } catch (error) {
    return String(obj);
  }
}
function createTerminalOutput(level, message, timestamp, prefix, stackInfo, presetStyles, presetName) {
  const location = stackInfo ? `${stackInfo.file}:${stackInfo.line}:${stackInfo.column}` : void 0;
  const ansiStyle = adaptToTerminal(
    level,
    message,
    timestamp,
    prefix,
    location,
    presetStyles,
    presetName
  );
  return [ansiStyle.text];
}
const isNode = typeof process !== "undefined" && process.versions && process.versions.node;
const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";
function supportsCSSColors() {
  return isBrowser;
}
function supportsANSIColors() {
  if (isNode) {
    return process.stdout?.isTTY === true || process.env.FORCE_COLOR === "1" || process.env.FORCE_COLOR === "true";
  }
  return false;
}
export {
  ANSI as A,
  BUILD_PRESETS as B,
  getAdaptiveColor as C,
  DEFAULT_CONFIG as D,
  ENVIRONMENT_DETECTION as E,
  ADAPTIVE_COLORS as F,
  BUFFER_LIMITS as G,
  generateLogId as H,
  parseTimeInput as I,
  formatDisplayTime as J,
  safeStringify as K,
  LEVEL_STYLES as L,
  escapeHtml as M,
  EXPORT_FORMATS as N,
  setupThemeChangeListener as O,
  StylePresets as S,
  TerminalRenderer as T,
  isBrowser as a,
  StyleBuilder as b,
  hexTo256 as c,
  detectEnvironmentPreset as d,
  colorToHex as e,
  formatTimestamp as f,
  getOptimalConfig as g,
  hexToRgb as h,
  isNode as i,
  getANSIForeground as j,
  getANSIBackground as k,
  getANSI256Foreground as l,
  getANSI256Background as m,
  cssColorToANSI as n,
  getEnvironment as o,
  parseStackTrace as p,
  isRunningInTerminal as q,
  rgbToHex as r,
  supportsANSI as s,
  getColorCapability as t,
  getTerminalWidth as u,
  getTerminalHeight as v,
  getEnvironmentInfo as w,
  supportsCSSColors as x,
  createStyledOutput as y,
  supportsANSIColors as z
};
//# sourceMappingURL=environment-COWvu6Wz.js.map
